import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { track } from "@/lib/analytics";

// Fires on every route change and scrolls the window to the top.
// Emits a $pageview event to PostHog + a standard Meta Pixel PageView.
//
// Meta Pixel PageView protocol:
//   - The initial "PageView" is fired synchronously by the base pixel snippet
//     in `public/index.html` (this is Meta's canonical initial-load event).
//   - THIS hook fires `fbq('track', 'PageView')` ONLY on subsequent client-side
//     route changes — never on the first mount (that would be a duplicate of
//     the base-code fire and confuses Meta's classifier into bucketing our
//     PageViews as "Custom").
//   - Duplicate fires from React.StrictMode double-invoke and same-URL
//     re-renders are also skipped via a ref-tracked last-URL guard.
export default function ScrollToTop() {
  const { pathname, hash, key, search } = useLocation();
  const lastPageviewRef = useRef("");
  const initialSkippedRef = useRef(false);

  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash, key]);

  useEffect(() => {
    const url = `${pathname}${search || ""}`;

    // Skip the very first mount — index.html already fired PageView for it.
    if (!initialSkippedRef.current) {
      initialSkippedRef.current = true;
      lastPageviewRef.current = url;
      return;
    }

    // Dedupe: same URL as last fire (React.StrictMode double-invoke, re-render).
    if (lastPageviewRef.current === url) return;
    lastPageviewRef.current = url;

    // PostHog
    track("$pageview", { path: pathname, search });

    // Meta Pixel — official standard PageView event (subsequent route changes only).
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [pathname, search]);

  return null;
}
